import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from '../../utils/i18n';
import ErrorDisplay from './ErrorDisplay';
import LoadingSpinner from './LoadingSpinner';
import AuthLanguageSwitcher from './AuthLanguageSwitcher';
import { WarningCircle, CheckCircle, ArrowLeft } from '@phosphor-icons/react';
import { ValidationError } from '../../types/auth';

type ForgotPasswordFormData = {
  email: string;
};

const ForgotPasswordForm: React.FC = () => {
  const { forgotPassword, isLoading, error, clearError, retryLastAction } = useAuth();
  const { t } = useTranslation('auth');
  const [isSubmitted, setIsSubmitted] = useState(false);

  const forgotPasswordSchema = useMemo(
    () =>
      z.object({
        email: z
          .string()
          .min(1, t('validation.emailRequired'))
          .email(t('validation.emailInvalid')),
      }),
    [t]
  );

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: { email: '' },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await forgotPassword(data.email);
      setIsSubmitted(true);
    } catch (error) {
      // Surface server-side field errors next to the input when available.
      const fieldErrors = (error as any)?.details?.errors as ValidationError[] | undefined;
      if (Array.isArray(fieldErrors)) {
        fieldErrors.forEach(({ field, message }) => {
          if (field === 'email') {
            setError('email', { type: 'manual', message: message || t('forgot.invalidEmail') });
          }
        });
      }
    }
  };

  if (isSubmitted) {
    return (
      <div className="auth-background">
        <div className="auth-background-overlay"></div>
        <AuthLanguageSwitcher />
        <div className="auth-card">
          <div className="auth-header">
            <div className="auth-logo">
              <img src="/logo-commission-fr.png" alt="CNCLT" />
            </div>
            <h1 className="auth-title">{t('forgot.submittedTitle')}</h1>
            <p className="auth-subtitle">{t('forgot.submittedSubtitle')}</p>
          </div>

          <div className="auth-alert auth-alert-success" role="status">
            <span className="auth-alert-icon">
              <CheckCircle size={20} weight="fill" aria-hidden />
            </span>
            <p className="auth-alert-body">
              {t('forgot.submittedBody')}
            </p>
          </div>

          <div className="text-center">
            <Link to="/login" className="auth-button-primary">
              <ArrowLeft size={18} weight="bold" aria-hidden />
              {t('common.backToLogin')}
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-background">
      <div className="auth-background-overlay"></div>
      <AuthLanguageSwitcher />
      <div className="auth-card">
        <div className="auth-header">
          <div className="auth-logo">
            <img src="/logo-commission-fr.png" alt="CNCLT" />
          </div>
          <h1 className="auth-title">{t('forgot.title')}</h1>
          <p className="auth-subtitle">{t('forgot.subtitle')}</p>
        </div>

        <ErrorDisplay
          error={error}
          onRetry={retryLastAction}
          onDismiss={clearError}
        />

        <form className="auth-form" onSubmit={handleSubmit(onSubmit)}>
          <div className="auth-form-group">
            <label className="auth-form-label">{t('common.emailLabel')}</label>
            <input
              type="email"
              className={`auth-form-input ${errors.email ? 'auth-form-input-error' : ''}`}
              placeholder={t('common.emailPlaceholder')}
              disabled={isLoading}
              {...register('email')}
            />
            {errors.email && (
              <p className="auth-form-error" role="alert">
                <WarningCircle size={14} weight="bold" aria-hidden />
                {errors.email.message}
              </p>
            )}
          </div>

          <button
            type="submit"
            className={`auth-button-primary ${isLoading ? 'auth-loading' : ''}`}
            disabled={isLoading}
          >
            {isLoading ? (
              <LoadingSpinner size="sm" text={t('forgot.submitting')} />
            ) : (
              t('forgot.submit')
            )}
          </button>

          <div className="text-center">
            <Link to="/login" className="auth-link text-sm">
              {t('common.backToLogin')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ForgotPasswordForm;
